/**
 * GSTReportScreen — Monthly GST summary with GSTR-1 JSON + GSTR-3B.
 *
 * Sources every figure from the shared `buildGstReturns` aggregator so the
 * on-screen numbers, the PDF, and the government GSTR-1 JSON can never drift.
 *
 * Shows:
 *   • GSTR-3B outward summary (net of credit notes) + tax by component
 *   • Data-quality warnings a CA would flag before filing
 *   • B2B invoice list (customers with GSTIN)
 *   • B2C (small) rate-wise aggregate
 *   • HSN-wise summary
 * Exports: PDF (human) and GSTR-1 JSON (portal upload).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Download, FileJson, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { usePlanGate, FEATURE_GST_REPORTS } from '@/plans';
import { usePermissions, INVENTORY_EDIT } from '@/rbac';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { HsnFixDialog } from '@/screens/inventory/HsnFixDialog';
import { loadGstMonth } from '@/reports/gst/gstDataSource';
import { loadBooksPurchases, sumPurchaseItc } from '@/reports/gst/purchaseDataSource';
import type { BooksPurchase } from '@/reports/gst/gstr2b';
import { buildGstReturns, blockingWarnings, type GstReturnDataset } from '@/reports/gst/gstReturns';
import { computeGstr3bSetOff, addHeads, totalHeads, type TaxHeads } from '@/reports/gst/gst3b';
import { stringifyGstr1 } from '@/reports/gst/gstr1Json';
import { stateLabel } from '@/reports/gst/gstStates';
import { useSettingsStore } from '@/store/useSettingsStore';
import { toCsv } from '@/utils/csv';
import { escapeHtml } from '@/utils/escapeHtml';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Meta {
  storeName: string;
  gstin: string | null;
  homeStateCode: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const OPENING_ITC_KEYS = {
  igst: 'GST_OPENING_ITC_IGST',
  cgst: 'GST_OPENING_ITC_CGST',
  sgst: 'GST_OPENING_ITC_SGST',
  cess: 'GST_OPENING_ITC_CESS',
} as const;

const NIL_CATEGORY_LABELS: Record<string, string> = {
  INTER_REG: 'Inter-State, registered',
  INTRA_REG: 'Intra-State, registered',
  INTER_UNREG: 'Inter-State, unregistered',
  INTRA_UNREG: 'Intra-State, unregistered',
};
function nilCategoryLabel(category: string): string {
  return NIL_CATEGORY_LABELS[category] ?? category;
}

function monthName(m: number): string {
  return new Date(2024, m, 1).toLocaleString('en-IN', { month: 'long' });
}

// ─── Component ───────────────────────────────────────────────────────────────

export function GSTReportScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const { canAccess } = usePlanGate();
  const { can } = usePermissions();
  const [showHsnFix, setShowHsnFix] = useState(false);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [ds, setDs] = useState<GstReturnDataset | null>(null);
  const [periodItc, setPeriodItc] = useState<TaxHeads | null>(null);
  const [purchases, setPurchases] = useState<BooksPurchase[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<null | 'pdf' | 'json'>(null);

  // Opening input-credit ledger balance — the merchant's carried-forward ITC
  // from prior returns. We don't track it (no filing ledger yet), so it's entered
  // here and persisted per store; it feeds the set-off so net-payable is real.
  const getNumber = useSettingsStore((s) => s.getNumber);
  const setSetting = useSettingsStore((s) => s.setSetting);
  const [openingItc, setOpeningItc] = useState<TaxHeads>({
    igst: getNumber(OPENING_ITC_KEYS.igst),
    cgst: getNumber(OPENING_ITC_KEYS.cgst),
    sgst: getNumber(OPENING_ITC_KEYS.sgst),
    cess: getNumber(OPENING_ITC_KEYS.cess),
  });

  const updateOpening = (head: keyof TaxHeads, value: number) => {
    const v = Number.isFinite(value) && value >= 0 ? value : 0;
    setOpeningItc((prev) => ({ ...prev, [head]: v }));
    setSetting(OPENING_ITC_KEYS[head], String(v));
  };

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const src = await loadGstMonth(activeStoreId, year, month);
      const dataset = buildGstReturns(src.invoices, { homeStateCode: src.homeStateCode ?? '', sellerGstin: src.gstin });
      // Purchases for the same month drive Table 4 ITC and the ITC register.
      const rows = await loadBooksPurchases(
        activeStoreId,
        new Date(year, month, 1),
        new Date(year, month + 1, 0, 23, 59, 59, 999),
      );
      setMeta({ storeName: src.storeName, gstin: src.gstin, homeStateCode: src.homeStateCode });
      setDs(dataset);
      setPurchases(rows);
      setPeriodItc(sumPurchaseItc(rows));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, year, month]);

  const setOff = useMemo(
    () => (ds && periodItc
      ? computeGstr3bSetOff(
          { igst: ds.gstr3b.igst, cgst: ds.gstr3b.cgst, sgst: ds.gstr3b.sgst, cess: ds.gstr3b.cess },
          addHeads(periodItc, openingItc),
        )
      : null),
    [ds, periodItc, openingItc],
  );

  useEffect(() => { void load(); }, [load]);

  function prev() {
    if (month === 0) { setYear((y) => y - 1); setMonth(11); }
    else { setMonth((m) => m - 1); }
  }
  function next() {
    const now = new Date();
    if (year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth())) return;
    if (month === 11) { setYear((y) => y + 1); setMonth(0); }
    else { setMonth((m) => m + 1); }
  }

  const handleExportPdf = async () => {
    if (!ds || !meta) return;
    setExporting('pdf');
    try {
      const html = buildGstHtml(ds, meta, year, month);
      await pdf.saveFromHtml({ html, defaultFileName: `GSTReport_${year}_${String(month + 1).padStart(2, '0')}.pdf` });
    } finally {
      setExporting(null);
    }
  };

  const handleExportItcCsv = async () => {
    if (purchases.length === 0) return;
    setExporting('pdf'); // reuse the busy flag; disables the buttons
    try {
      const csv = toCsv(purchases, [
        { header: 'Supplier', value: (p) => p.supplierName },
        { header: 'Supplier GSTIN', value: (p) => p.supplierGstin },
        { header: 'Bill No', value: (p) => p.invoiceNumber },
        { header: 'Date', value: (p) => (p.date ? p.date.toLocaleDateString('en-IN') : '') },
        { header: 'Taxable', value: (p) => p.taxable },
        { header: 'IGST', value: (p) => p.igst },
        { header: 'CGST', value: (p) => p.cgst },
        { header: 'SGST', value: (p) => p.sgst },
        { header: 'Total ITC', value: (p) => p.totalTax },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `ITC_Register_${year}_${String(month + 1).padStart(2, '0')}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(null);
    }
  };

  const handleExportJson = async () => {
    if (!ds || !meta || !meta.gstin) return;
    // Guard as well as disable: the dataset can change under a stale click.
    if (blockingWarnings(ds).length > 0) return;
    setExporting('json');
    try {
      const json = stringifyGstr1(ds, { gstin: meta.gstin, month, year });
      await file.saveText({
        content: json,
        defaultFileName: `GSTR1_${meta.gstin}_${String(month + 1).padStart(2, '0')}${year}.json`,
        filters: [{ name: 'GST JSON', extensions: ['json'] }],
      });
    } finally {
      setExporting(null);
    }
  };

  if (!canAccess(FEATURE_GST_REPORTS)) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-heading text-fg-primary">GST Reports require a higher plan</div>
          <div className="mt-1 text-body text-fg-tertiary">Upgrade to Professional or Enterprise to access GST summaries.</div>
        </div>
      </div>
    );
  }

  const b2cTaxable = ds ? ds.b2cs.reduce((s, r) => s + r.taxableValue, 0) : 0;
  const b2cTax = ds ? ds.b2cs.reduce((s, r) => s + r.igst + r.cgst + r.sgst + r.cess, 0) : 0;

  const blockers = useMemo(() => (ds ? blockingWarnings(ds) : []), [ds]);
  const jsonBlockedReason =
    !meta?.gstin ? 'Add your GSTIN in Settings to export GSTR-1 JSON'
    : blockers.length > 0
      ? `Fix ${blockers.length} data issue${blockers.length === 1 ? '' : 's'} before exporting — the GST portal will reject this file.`
      : null;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Month picker + export */}
      <div className="flex items-center gap-3">
        <button type="button" onClick={prev} title="Previous month" aria-label="Previous month"
          className="rounded-md p-1.5 text-fg-secondary hover:bg-bg-subtle transition-colors">
          <ChevronLeft size={16} />
        </button>
        <span className="text-body font-medium text-fg-primary">{monthName(month)} {year}</span>
        <button type="button" onClick={next} title="Next month" aria-label="Next month"
          className="rounded-md p-1.5 text-fg-secondary hover:bg-bg-subtle transition-colors">
          <ChevronRight size={16} />
        </button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!ds || exporting !== null}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleExportJson()}
            disabled={!ds || exporting !== null || jsonBlockedReason !== null}
            title={jsonBlockedReason ?? 'Export GSTR-1 JSON for portal upload'}
          >
            <FileJson size={14} className="mr-1.5" /> {exporting === 'json' ? 'Exporting…' : 'GSTR-1 JSON'}
          </Button>
        </div>
      </div>

      {!loading && meta && !meta.gstin && (
        <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-2 text-label text-warning-ink">
          Add your GSTIN in Settings to enable GSTR-1 JSON export.
        </div>
      )}

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !ds || !meta ? null : (
        <>
          {/* Tax summary */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Taxable value" value={formatCurrency(ds.totals.taxableValue)} />
            <KPI label="CGST" value={formatCurrency(ds.totals.cgst)} />
            <KPI label="SGST" value={formatCurrency(ds.totals.sgst)} />
            <KPI label={ds.totals.igst > 0 ? 'IGST' : 'Cess'} value={formatCurrency(ds.totals.igst > 0 ? ds.totals.igst : ds.totals.cess)} />
          </div>
          <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-label uppercase text-fg-tertiary">Total tax liability</div>
              <div className="text-heading text-accent-ink tabular-nums">{formatCurrency(ds.totals.totalTax)}</div>
            </div>
            <div className="text-right">
              <div className="text-label uppercase text-fg-tertiary">{ds.totals.saleInvoiceCount} invoices</div>
              <div className="text-body text-fg-secondary">
                {meta.storeName} · {meta.gstin ?? '—'}{meta.homeStateCode ? ` · ${stateLabel(meta.homeStateCode)}` : ''}
              </div>
            </div>
          </div>

          {/* Data-quality warnings */}
          {ds.warnings.length > 0 && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-body font-medium text-warning-ink">
                  <AlertTriangle size={16} /> {ds.warnings.length} issue{ds.warnings.length === 1 ? '' : 's'} to fix before filing
                  {blockers.length > 0 && (
                    <span className="text-label uppercase text-danger-ink">
                      · GSTR-1 JSON export blocked
                    </span>
                  )}
                </div>
                {activeStoreId && can(INVENTORY_EDIT) && ds.warnings.some((w) => w.code === 'MISSING_HSN' || w.code === 'INVALID_RATE') && (
                  <button
                    type="button"
                    onClick={() => setShowHsnFix(true)}
                    className="shrink-0 rounded-md border border-warning/40 px-2.5 py-1 text-label uppercase text-warning-ink hover:bg-warning/10 transition-colors"
                  >
                    Fix product HSN →
                  </button>
                )}
              </div>
              <ul className="space-y-1 text-label text-fg-secondary">
                {ds.warnings.slice(0, 8).map((w, i) => (
                  <li key={i}>
                    {w.invoiceNumber && <span className="font-mono text-fg-tertiary">{w.invoiceNumber} — </span>}
                    {w.detail}
                  </li>
                ))}
                {ds.warnings.length > 8 && <li className="text-fg-tertiary">+{ds.warnings.length - 8} more…</li>}
              </ul>
            </div>
          )}

          {/* GSTR-3B outward summary */}
          <Section title="GSTR-3B · 3.1(a) Outward taxable supplies (net of credit notes)">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Mini label="Taxable" value={formatCurrency(ds.gstr3b.taxableValue)} />
              <Mini label="IGST" value={formatCurrency(ds.gstr3b.igst)} />
              <Mini label="CGST" value={formatCurrency(ds.gstr3b.cgst)} />
              <Mini label="SGST" value={formatCurrency(ds.gstr3b.sgst)} />
              <Mini label="Cess" value={formatCurrency(ds.gstr3b.cess)} />
            </div>
            {ds.gstr3b.nilExemptSupplies > 0 && (
              <div className="mt-2 flex items-center justify-between rounded-md bg-bg-subtle px-3 py-1.5 text-label text-fg-secondary">
                <span>3.1(c) Nil-rated / exempt outward supplies</span>
                <span className="tabular-nums text-fg-primary">{formatCurrency(ds.gstr3b.nilExemptSupplies)}</span>
              </div>
            )}
            {ds.totals.creditNoteCount > 0 && (
              <div className="mt-2 text-label text-fg-tertiary">
                Net of {ds.totals.creditNoteCount} credit note{ds.totals.creditNoteCount === 1 ? '' : 's'}.
              </div>
            )}
            {(ds.gstr3b.carryForward.taxableValue > 0
              || ds.gstr3b.carryForward.igst > 0
              || ds.gstr3b.carryForward.cgst > 0
              || ds.gstr3b.carryForward.sgst > 0
              || ds.gstr3b.carryForward.cess > 0) && (
              <div className="mt-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-label text-warning-ink">
                Credit notes exceed sales this period. 3.1(a) is floored at zero; the excess
                carries forward — taxable {formatCurrency(ds.gstr3b.carryForward.taxableValue)},
                tax {formatCurrency(ds.gstr3b.carryForward.igst + ds.gstr3b.carryForward.cgst + ds.gstr3b.carryForward.sgst + ds.gstr3b.carryForward.cess)}.
              </div>
            )}
          </Section>

          {/* GSTR-3B · ITC set-off and net tax payable */}
          {setOff && (
            <Section title="GSTR-3B · Tax payable after input credit set-off">
              <div className="mb-3 rounded-md border border-stroke-hairline/10 bg-bg-subtle px-3 py-2">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-label uppercase text-fg-tertiary">Opening ITC balance (credit ledger)</span>
                  <span className="text-label text-fg-tertiary">from your last filed return</span>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {(['igst', 'cgst', 'sgst', 'cess'] as const).map((h) => (
                    <label key={h} className="flex items-center gap-1.5">
                      <span className="w-10 text-label uppercase text-fg-secondary">{h}</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={openingItc[h] || ''}
                        onChange={(e) => updateOpening(h, parseFloat(e.target.value))}
                        placeholder="0"
                        className="w-full rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary tabular-nums outline-none focus:border-accent/40"
                      />
                    </label>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-body">
                  <thead>
                    <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                      <th className="py-1 text-left font-normal">Head</th>
                      <th className="py-1 text-right font-normal">Liability (3.1)</th>
                      <th className="py-1 text-right font-normal">ITC avail. (opening + Table 4)</th>
                      <th className="py-1 text-right font-normal">ITC used</th>
                      <th className="py-1 text-right font-normal">Payable in cash</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['igst', 'cgst', 'sgst', 'cess'] as const).map((h) => (
                      <tr key={h} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5 uppercase text-fg-secondary">{h}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(setOff.liability[h])}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(setOff.itcAvailable[h])}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(setOff.itcUtilised[h])}</td>
                        <td className={cn('py-1.5 text-right tabular-nums font-medium', setOff.cashPayable[h] > 0 ? 'text-fg-primary' : 'text-fg-tertiary')}>
                          {formatCurrency(setOff.cashPayable[h])}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-stroke-hairline/20">
                      <td className="py-1.5 font-medium text-fg-primary">Total</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(totalHeads(setOff.liability))}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(totalHeads(setOff.itcAvailable))}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(totalHeads(setOff.itcUtilised))}</td>
                      <td className="py-1.5 text-right tabular-nums font-semibold text-accent-ink">{formatCurrency(totalHeads(setOff.cashPayable))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-label text-fg-tertiary">
                {totalHeads(setOff.itcCarriedForward) > 0 && (
                  <span>ITC carried forward: <span className="text-fg-secondary">{formatCurrency(totalHeads(setOff.itcCarriedForward))}</span></span>
                )}
                <span>Blocked ITC under §17(5) is not yet excluded — review before filing.</span>
              </div>
            </Section>
          )}

          {/* ITC Register — purchase-wise input credit (Table 4 detail) */}
          {purchases.length > 0 && (
            <Section title={`Input Tax Credit — purchase-wise (${purchases.length})`}>
              <div className="mb-2 flex justify-end">
                <Button variant="secondary" size="sm" onClick={() => void handleExportItcCsv()} disabled={exporting !== null}>
                  <Download size={14} className="mr-1.5" /> CSV
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-body">
                  <thead>
                    <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                      <th className="py-1 text-left font-normal">Supplier / GSTIN</th>
                      <th className="py-1 text-left font-normal">Bill</th>
                      <th className="py-1 text-right font-normal">Taxable</th>
                      <th className="py-1 text-right font-normal">IGST</th>
                      <th className="py-1 text-right font-normal">CGST</th>
                      <th className="py-1 text-right font-normal">SGST</th>
                      <th className="py-1 text-right font-normal">Total ITC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((p, i) => (
                      <tr key={i} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5 text-fg-primary">{p.supplierName}<br /><span className="font-mono text-label text-fg-tertiary">{p.supplierGstin ?? '—'}</span></td>
                        <td className="py-1.5 font-mono text-label text-fg-secondary">{p.invoiceNumber}<br /><span className="text-fg-tertiary">{p.date ? p.date.toLocaleDateString('en-IN') : '—'}</span></td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(p.taxable)}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(p.igst)}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(p.cgst)}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(p.sgst)}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-primary">{formatCurrency(p.totalTax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {periodItc && (
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-label text-fg-tertiary">
                  <span>Table 4 ITC — IGST {formatCurrency(periodItc.igst)} · CGST {formatCurrency(periodItc.cgst)} · SGST {formatCurrency(periodItc.sgst)}</span>
                </div>
              )}
            </Section>
          )}

          {/* B2B */}
          {ds.b2b.length > 0 && (
            <Section title={`B2B Invoices (${ds.totals.b2bInvoiceCount})`}>
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="py-1 text-left font-normal">Invoice</th>
                    <th className="py-1 text-left font-normal">Customer / GSTIN</th>
                    <th className="py-1 text-left font-normal">POS</th>
                    <th className="py-1 text-right font-normal">Taxable</th>
                    <th className="py-1 text-right font-normal">Tax</th>
                    <th className="py-1 text-right font-normal">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {ds.b2b.flatMap((party) =>
                    party.invoices.map((iv, i) => {
                      const taxable = iv.items.reduce((s, it) => s + it.taxableValue, 0);
                      const tax = iv.items.reduce((s, it) => s + it.igst + it.cgst + it.sgst + it.cess, 0);
                      return (
                        <tr key={`${party.ctin}-${i}`} className="border-b border-stroke-hairline/10">
                          <td className="py-1.5 font-mono text-label text-fg-primary">{iv.invoiceNumber}<br /><span className="text-fg-tertiary">{iv.date.toLocaleDateString('en-IN')}</span></td>
                          <td className="py-1.5 text-fg-primary">{party.customerName}<br /><span className="font-mono text-label text-fg-tertiary">{party.ctin}</span></td>
                          <td className="py-1.5 text-label text-fg-secondary">{stateLabel(iv.posCode)}</td>
                          <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(taxable)}</td>
                          <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(tax)}</td>
                          <td className="py-1.5 text-right text-fg-primary tabular-nums">{formatCurrency(iv.invoiceValue)}</td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </Section>
          )}

          {/* B2C Large (Table 5) — inter-state B2C invoices above the threshold */}
          {ds.b2cl.length > 0 && (
            <Section title={`B2C Large (${ds.totals.b2clInvoiceCount})`}>
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="py-1 text-left font-normal">Invoice</th>
                    <th className="py-1 text-left font-normal">POS</th>
                    <th className="py-1 text-right font-normal">Taxable</th>
                    <th className="py-1 text-right font-normal">Tax</th>
                    <th className="py-1 text-right font-normal">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {ds.b2cl.flatMap((p) =>
                    p.invoices.map((iv, i) => {
                      const taxable = iv.items.reduce((s, it) => s + it.taxableValue, 0);
                      const tax = iv.items.reduce((s, it) => s + it.igst + it.cgst + it.sgst + it.cess, 0);
                      return (
                        <tr key={`${p.posCode}-${i}`} className="border-b border-stroke-hairline/10">
                          <td className="py-1.5 font-mono text-label text-fg-primary">{iv.invoiceNumber}<br /><span className="text-fg-tertiary">{iv.date.toLocaleDateString('en-IN')}</span></td>
                          <td className="py-1.5 text-label text-fg-secondary">{stateLabel(p.posCode)}</td>
                          <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(taxable)}</td>
                          <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(tax)}</td>
                          <td className="py-1.5 text-right text-fg-primary tabular-nums">{formatCurrency(iv.invoiceValue)}</td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </Section>
          )}

          {/* B2C small — rate-wise */}
          {ds.b2cs.length > 0 && (
            <Section title="B2C (Small) — rate-wise aggregate">
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="py-1 text-left font-normal">Type</th>
                    <th className="py-1 text-left font-normal">POS</th>
                    <th className="py-1 text-right font-normal">Rate</th>
                    <th className="py-1 text-right font-normal">Taxable</th>
                    <th className="py-1 text-right font-normal">Tax</th>
                  </tr>
                </thead>
                <tbody>
                  {ds.b2cs.map((r, i) => (
                    <tr key={i} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-label text-fg-secondary">{r.supplyType}</td>
                      <td className="py-1.5 text-label text-fg-secondary">{stateLabel(r.posCode)}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{r.rate}%</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(r.taxableValue)}</td>
                      <td className="py-1.5 text-right text-fg-primary tabular-nums">{formatCurrency(r.igst + r.cgst + r.sgst + r.cess)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 flex gap-8 text-label text-fg-tertiary">
                <span>Taxable {formatCurrency(b2cTaxable)}</span>
                <span>Tax {formatCurrency(b2cTax)}</span>
              </div>
            </Section>
          )}

          {/* Nil / exempt / non-GST (Table 8) */}
          {ds.nilExempt.length > 0 && (
            <Section title="Nil-rated, Exempt & Non-GST (Table 8)">
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="py-1 text-left font-normal">Supply type</th>
                    <th className="py-1 text-right font-normal">Nil-rated</th>
                    <th className="py-1 text-right font-normal">Exempt</th>
                    <th className="py-1 text-right font-normal">Non-GST</th>
                  </tr>
                </thead>
                <tbody>
                  {ds.nilExempt.map((r, i) => (
                    <tr key={i} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-fg-secondary">{nilCategoryLabel(r.category)}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(r.nilRated)}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(r.exempt)}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(r.nonGst)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* Credit notes — registered (CDNR) */}
          {ds.cdnr.length > 0 && (
            <Section title={`Credit Notes — Registered (${ds.totals.cdnrNoteCount})`}>
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="py-1 text-left font-normal">Note</th>
                    <th className="py-1 text-left font-normal">Customer / GSTIN</th>
                    <th className="py-1 text-left font-normal">POS</th>
                    <th className="py-1 text-right font-normal">Taxable</th>
                    <th className="py-1 text-right font-normal">Tax</th>
                    <th className="py-1 text-right font-normal">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {ds.cdnr.flatMap((party) =>
                    party.notes.map((n, i) => {
                      const taxable = n.items.reduce((s, it) => s + it.taxableValue, 0);
                      const tax = n.items.reduce((s, it) => s + it.igst + it.cgst + it.sgst + it.cess, 0);
                      return (
                        <tr key={`${party.ctin}-${i}`} className="border-b border-stroke-hairline/10">
                          <td className="py-1.5 font-mono text-label text-fg-primary">{n.noteNumber}<br /><span className="text-fg-tertiary">{n.date.toLocaleDateString('en-IN')}</span></td>
                          <td className="py-1.5 text-fg-primary">{party.customerName}<br /><span className="font-mono text-label text-fg-tertiary">{party.ctin}</span></td>
                          <td className="py-1.5 text-label text-fg-secondary">{stateLabel(n.posCode)}</td>
                          <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(taxable)}</td>
                          <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(tax)}</td>
                          <td className="py-1.5 text-right text-fg-primary tabular-nums">{formatCurrency(n.value)}</td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </Section>
          )}

          {/* Credit notes — unregistered B2CL (CDNUR) */}
          {ds.cdnur.length > 0 && (
            <Section title={`Credit Notes — Unregistered B2CL (${ds.totals.cdnurNoteCount})`}>
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="py-1 text-left font-normal">Note</th>
                    <th className="py-1 text-left font-normal">POS</th>
                    <th className="py-1 text-right font-normal">Taxable</th>
                    <th className="py-1 text-right font-normal">Tax</th>
                    <th className="py-1 text-right font-normal">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {ds.cdnur.map((n, i) => {
                    const taxable = n.items.reduce((s, it) => s + it.taxableValue, 0);
                    const tax = n.items.reduce((s, it) => s + it.igst + it.cgst + it.sgst + it.cess, 0);
                    return (
                      <tr key={i} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5 font-mono text-label text-fg-primary">{n.noteNumber}<br /><span className="text-fg-tertiary">{n.date.toLocaleDateString('en-IN')}</span></td>
                        <td className="py-1.5 text-label text-fg-secondary">{stateLabel(n.posCode)}</td>
                        <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(taxable)}</td>
                        <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(tax)}</td>
                        <td className="py-1.5 text-right text-fg-primary tabular-nums">{formatCurrency(n.value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Section>
          )}

          {/* HSN summary */}
          {ds.hsn.length > 0 && (
            <Section title="HSN-wise Summary">
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="py-1 text-left font-normal">HSN</th>
                    <th className="py-1 text-right font-normal">Qty</th>
                    <th className="py-1 text-right font-normal">Taxable</th>
                    <th className="py-1 text-right font-normal">CGST</th>
                    <th className="py-1 text-right font-normal">SGST</th>
                    <th className="py-1 text-right font-normal">IGST</th>
                  </tr>
                </thead>
                <tbody>
                  {ds.hsn.map((row, i) => (
                    <tr key={i} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 font-mono text-label text-fg-primary">{row.hsn || '—'}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{row.quantity}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(row.taxableValue)}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(row.cgst)}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(row.sgst)}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{formatCurrency(row.igst)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </>
      )}

      {activeStoreId && (
        <HsnFixDialog
          open={showHsnFix}
          onOpenChange={setShowHsnFix}
          storeId={activeStoreId}
          onApplied={() => void load()}
        />
      )}
    </div>
  );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

function KPI({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className="mt-1 text-heading tabular-nums text-fg-primary">{value}</div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className="text-body tabular-nums text-fg-primary">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF ─────────────────────────────────────────────────────────────────────

function buildGstHtml(ds: GstReturnDataset, meta: Meta, year: number, month: number): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const b2bRowsHtml = ds.b2b.flatMap((party) =>
    party.invoices.map((iv) => {
      const taxable = iv.items.reduce((s, it) => s + it.taxableValue, 0);
      const tax = iv.items.reduce((s, it) => s + it.igst + it.cgst + it.sgst + it.cess, 0);
      return `<tr>
        <td>${escapeHtml(iv.invoiceNumber)}<br/><small>${iv.date.toLocaleDateString('en-IN')}</small></td>
        <td>${escapeHtml(party.customerName)}<br/><small>${escapeHtml(party.ctin)}</small></td>
        <td>${stateLabel(iv.posCode)}</td>
        <td align="right">${fmt(taxable)}</td>
        <td align="right">${fmt(tax)}</td>
        <td align="right">${fmt(iv.invoiceValue)}</td>
      </tr>`;
    }),
  ).join('');
  const hsnRowsHtml = ds.hsn.map((r) => `<tr>
    <td>${escapeHtml(r.hsn || '—')}</td><td align="right">${r.quantity}</td><td align="right">${fmt(r.taxableValue)}</td>
    <td align="right">${fmt(r.cgst)}</td><td align="right">${fmt(r.sgst)}</td><td align="right">${fmt(r.igst)}</td>
  </tr>`).join('');
  const b2csRowsHtml = ds.b2cs.map((r) => `<tr>
    <td>${escapeHtml(r.supplyType)}</td><td>${stateLabel(r.posCode)}</td><td align="right">${r.rate}%</td>
    <td align="right">${fmt(r.taxableValue)}</td><td align="right">${fmt(r.igst + r.cgst + r.sgst + r.cess)}</td>
  </tr>`).join('');
  const nilRowsHtml = ds.nilExempt.map((r) => `<tr>
    <td>${nilCategoryLabel(r.category)}</td><td align="right">${fmt(r.nilRated)}</td>
    <td align="right">${fmt(r.exempt)}</td><td align="right">${fmt(r.nonGst)}</td>
  </tr>`).join('');
  const b2clRowsHtml = ds.b2cl.flatMap((p) =>
    p.invoices.map((iv) => {
      const taxable = iv.items.reduce((s, it) => s + it.taxableValue, 0);
      const tax = iv.items.reduce((s, it) => s + it.igst + it.cgst + it.sgst + it.cess, 0);
      return `<tr><td>${escapeHtml(iv.invoiceNumber)}<br/><small>${iv.date.toLocaleDateString('en-IN')}</small></td>
        <td>${stateLabel(p.posCode)}</td><td align="right">${fmt(taxable)}</td>
        <td align="right">${fmt(tax)}</td><td align="right">${fmt(iv.invoiceValue)}</td></tr>`;
    }),
  ).join('');
  const cdnrRowsHtml = ds.cdnr.flatMap((party) =>
    party.notes.map((n) => {
      const taxable = n.items.reduce((s, it) => s + it.taxableValue, 0);
      const tax = n.items.reduce((s, it) => s + it.igst + it.cgst + it.sgst + it.cess, 0);
      return `<tr><td>${escapeHtml(n.noteNumber)}<br/><small>${n.date.toLocaleDateString('en-IN')}</small></td>
        <td>${escapeHtml(party.customerName)}<br/><small>${escapeHtml(party.ctin)}</small></td><td>${stateLabel(n.posCode)}</td>
        <td align="right">${fmt(taxable)}</td><td align="right">${fmt(tax)}</td><td align="right">${fmt(n.value)}</td></tr>`;
    }),
  ).join('');
  const cdnurRowsHtml = ds.cdnur.map((n) => {
    const taxable = n.items.reduce((s, it) => s + it.taxableValue, 0);
    const tax = n.items.reduce((s, it) => s + it.igst + it.cgst + it.sgst + it.cess, 0);
    return `<tr><td>${escapeHtml(n.noteNumber)}<br/><small>${n.date.toLocaleDateString('en-IN')}</small></td>
      <td>${stateLabel(n.posCode)}</td><td align="right">${fmt(taxable)}</td><td align="right">${fmt(tax)}</td><td align="right">${fmt(n.value)}</td></tr>`;
  }).join('');

  const mName = new Date(year, month, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px}h2{font-size:14px;margin-top:24px;text-transform:uppercase;color:#666;border-bottom:1px solid #eee;padding-bottom:4px}
table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:5px 8px;border-bottom:1px solid #eee}th{text-align:left;color:#666;font-weight:500}
.kpi-grid{display:flex;gap:24px;margin:12px 0;flex-wrap:wrap}.kpi{min-width:110px}.kpi-label{font-size:11px;text-transform:uppercase;color:#888}.kpi-value{font-size:17px;font-weight:700}</style></head>
<body>
<h1>GST Report — ${mName}</h1>
<p style="color:#888">${escapeHtml(meta.storeName)} &middot; GSTIN: ${escapeHtml(meta.gstin ?? '—')} &middot; ${meta.homeStateCode ? stateLabel(meta.homeStateCode) : ''}</p>
<h2>GSTR-3B — Outward taxable supplies (net)</h2>
<div class="kpi-grid">
  <div class="kpi"><div class="kpi-label">Taxable</div><div class="kpi-value">${fmt(ds.gstr3b.taxableValue)}</div></div>
  <div class="kpi"><div class="kpi-label">IGST</div><div class="kpi-value">${fmt(ds.gstr3b.igst)}</div></div>
  <div class="kpi"><div class="kpi-label">CGST</div><div class="kpi-value">${fmt(ds.gstr3b.cgst)}</div></div>
  <div class="kpi"><div class="kpi-label">SGST</div><div class="kpi-value">${fmt(ds.gstr3b.sgst)}</div></div>
  <div class="kpi"><div class="kpi-label" style="color:#4F46E5">Total Tax</div><div class="kpi-value" style="color:#4F46E5">${fmt(ds.totals.totalTax)}</div></div>
${ds.gstr3b.nilExemptSupplies > 0 ? `  <div class="kpi"><div class="kpi-label">3.1(c) Nil/Exempt</div><div class="kpi-value">${fmt(ds.gstr3b.nilExemptSupplies)}</div></div>` : ''}
</div>
${ds.b2b.length > 0 ? `<h2>B2B Invoices</h2><table><tr><th>Invoice</th><th>Customer / GSTIN</th><th>POS</th><th align="right">Taxable</th><th align="right">Tax</th><th align="right">Total</th></tr>${b2bRowsHtml}</table>` : ''}
${ds.b2cl.length > 0 ? `<h2>B2C Large</h2><table><tr><th>Invoice</th><th>POS</th><th align="right">Taxable</th><th align="right">Tax</th><th align="right">Total</th></tr>${b2clRowsHtml}</table>` : ''}
${ds.b2cs.length > 0 ? `<h2>B2C (Small)</h2><table><tr><th>Type</th><th>POS</th><th align="right">Rate</th><th align="right">Taxable</th><th align="right">Tax</th></tr>${b2csRowsHtml}</table>` : ''}
${ds.nilExempt.length > 0 ? `<h2>Nil-rated, Exempt &amp; Non-GST</h2><table><tr><th>Supply type</th><th align="right">Nil-rated</th><th align="right">Exempt</th><th align="right">Non-GST</th></tr>${nilRowsHtml}</table>` : ''}
${ds.cdnr.length > 0 ? `<h2>Credit Notes — Registered</h2><table><tr><th>Note</th><th>Customer / GSTIN</th><th>POS</th><th align="right">Taxable</th><th align="right">Tax</th><th align="right">Value</th></tr>${cdnrRowsHtml}</table>` : ''}
${ds.cdnur.length > 0 ? `<h2>Credit Notes — Unregistered B2CL</h2><table><tr><th>Note</th><th>POS</th><th align="right">Taxable</th><th align="right">Tax</th><th align="right">Value</th></tr>${cdnurRowsHtml}</table>` : ''}
${ds.hsn.length > 0 ? `<h2>HSN Summary</h2><table><tr><th>HSN</th><th align="right">Qty</th><th align="right">Taxable</th><th align="right">CGST</th><th align="right">SGST</th><th align="right">IGST</th></tr>${hsnRowsHtml}</table>` : ''}
<p style="margin-top:40px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
